import React, {useCallback, useEffect, useState} from "react";
import {Select, Tag} from "antd";
import {Users as UsersIcon} from "lucide-react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {AdminLayout} from "../../components/layout/AdminLayout";
import {GenericTable} from "../../components/table/GenericTable";
import {AppDispatch, RootState} from "../../redux/store";
import {getApplicationUsers} from "../../redux/slice/userManagementSlice";
import {HeroHeader, Card, SearchField, COLORS} from "../../designSystem";

const statusOptions = [
  {label: "All users", value: ""},
  {label: "Active", value: "active"},
  {label: "Inactive", value: "inactive"},
  {label: "Deleted", value: "deleted"},
];

const verificationOptions = [
  {label: "All verification", value: ""},
  {label: "Verified", value: "true"},
  {label: "Unverified", value: "false"},
];

const dateText = (value: string) => value ? new Date(value).toLocaleString() : "-";

const userStatus = (row: any) => {
  if (row.isDeleted) return <Tag color="red">Deleted</Tag>;
  return row.isActive ? <Tag color="green">Active</Tag> : <Tag color="orange">Inactive</Tag>;
};

function Users() {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const {token} = useSelector((state: RootState) => state.account);
  const {users, summary, pageInfo, loading} = useSelector((state: RootState) => state.userManagement);
  const [searchParam, setSearchParam] = useState("");
  const [status, setStatus] = useState("");
  const [emailConfirmed, setEmailConfirmed] = useState("");

  const load = useCallback((pageNumber = 1) => {
    if (!token) return;
    dispatch(getApplicationUsers({token, searchParam, status, emailConfirmed, pageNumber}));
  }, [token, dispatch, searchParam, status, emailConfirmed]);

  useEffect(() => {
    load(1);
  }, [load]);

  return (
    <AdminLayout>
      <main className="h-full overflow-y-auto px-6 py-8 md:px-8" style={{background: COLORS.surface}}>
        <HeroHeader
          icon={UsersIcon}
          title="Users"
          subtitle={`${summary?.totalRecords || 0} customers found`}
        />

        <div className="grid gap-4 md:grid-cols-4 mt-8">
          <Card className="p-5">
            <div className="text-sm text-slate-500">Active</div>
            <div className="mt-2 text-2xl font-bold" style={{color: COLORS.success}}>{summary?.activeCount || 0}</div>
          </Card>
          <Card className="p-5">
            <div className="text-sm text-slate-500">Inactive</div>
            <div className="mt-2 text-2xl font-bold" style={{color: COLORS.warning}}>{summary?.inactiveCount || 0}</div>
          </Card>
          <Card className="p-5">
            <div className="text-sm text-slate-500">Deleted</div>
            <div className="mt-2 text-2xl font-bold" style={{color: COLORS.danger}}>{summary?.deletedCount || 0}</div>
          </Card>
          <Card className="p-5">
            <div className="text-sm text-slate-500">Unverified</div>
            <div className="mt-2 text-2xl font-bold" style={{color: COLORS.brand}}>{summary?.unverifiedCount || 0}</div>
          </Card>
        </div>

        <Card className="mt-8 p-5">
          <div className="grid gap-3 md:grid-cols-4">
            <SearchField
              className="md:col-span-2"
              placeholder="Search email, name or phone"
              value={searchParam}
              onChange={(event) => setSearchParam(event.target.value)}
            />
            <Select options={statusOptions} value={status} onChange={setStatus}/>
            <Select options={verificationOptions} value={emailConfirmed} onChange={setEmailConfirmed}/>
          </div>

          <GenericTable
            loading={loading}
            pageInfo={pageInfo}
            data={users}
            handlePageChange={load}
            columns={[
              {title: "Name", key: "name", render: (_: any, row: any) => `${row.firstName || ""} ${row.lastName || ""}`.trim() || "No name"},
              {title: "Email", key: "email", render: (_: any, row: any) => row.displayEmail || row.email || "-"},
              {title: "Phone", dataIndex: "phoneNumber", key: "phoneNumber", render: (value: string) => value || "-"},
              {title: "Status", key: "status", render: (_: any, row: any) => userStatus(row)},
              {title: "Email", dataIndex: "emailConfirmed", key: "emailConfirmed", render: (value: boolean) => value ? <Tag color="green">Verified</Tag> : <Tag color="orange">Unverified</Tag>},
              {title: "Source", dataIndex: "registrationSource", key: "registrationSource", render: (value: string) => value || "-"},
              {title: "Registered", dataIndex: "dateRegistered", key: "dateRegistered", render: dateText},
              {title: "Action", key: "action", render: (_: any, row: any) => (
                <button
                  onClick={() => navigate(`/users/${row.id}`)}
                  className="font-semibold"
                  style={{color: COLORS.brand}}
                >
                  View
                </button>
              )},
            ]}
          />
        </Card>
      </main>
    </AdminLayout>
  );
}

export default Users;
